/**
 * GalleryTile
 *
 * A single cell in the screenshot gallery grid: the thumbnail with a gradient
 * caption overlay carrying the key info (name + area). Clicking (or pressing
 * Enter/Space on) the tile opens the image in the lightbox.
 */

import { useState } from 'react';
import {
  asTrlDifficulty,
  TRL_DIFFICULTY_EMOJI,
  type TrlImageAsset,
} from '@swtorbit/shared-socket-events';
import { API_URL } from '../../../config';

interface GalleryTileProps {
  image: TrlImageAsset;
  onOpen: (image: TrlImageAsset) => void;
}

export function GalleryTile({ image, onOpen }: GalleryTileProps) {
  // Grid tiles load the small thumbnail; the lightbox keeps the full-res
  // original. If the thumbnail is missing (e.g. pre-backfill or a generation
  // failure) fall back to the original once so the tile never breaks.
  const originalSrc = `${API_URL}${image.url}`;
  const [src, setSrc] = useState(image.thumbUrl ? `${API_URL}${image.thumbUrl}` : originalSrc);
  // Unrated images keep the plain location pin rather than a difficulty glyph.
  const difficulty = asTrlDifficulty(image.difficulty);

  return (
    <button
      type="button"
      className="gallery-tile"
      onClick={() => onOpen(image)}
      title={image.name}
    >
      <img
        className="gallery-tile__img"
        src={src}
        alt={image.name}
        loading="lazy"
        decoding="async"
        onError={() => { if (src !== originalSrc) setSrc(originalSrc); }}
      />
      <div className="gallery-tile__caption">
        <span className="gallery-tile__title">{image.name}</span>
        <span className="gallery-tile__subtitle">{difficulty ? TRL_DIFFICULTY_EMOJI[difficulty] : '📍'} {image.area}</span>
      </div>
      {image.isOwn === false && <span className="gallery-tile__badge" title="Shared by another admin">🔗</span>}
      {image.isOwn === true && image.isShared === true && <span className="gallery-tile__badge" title="You have shared this item">🌏</span>}
      {!!image.usageCount && (
        <span className="gallery-tile__usage" title={`Used ${image.usageCount} time${image.usageCount === 1 ? '' : 's'}`}>
          {image.usageCount}
        </span>
      )}
    </button>
  );
}
